import React, { useEffect, useRef } from "react";
import httpService from "../../services/http.service";
import { getCMSApiRoute } from "../../utils/api.utils";
import _ from "lodash";
import { getAgeRangePublicRoute } from "../ageRange.util";

export function useCategoryContentList({
  fetchDelay = 0,
  slug,
  initialPage = 1,
  pageSize = 10,
  autoInit = false,
  initialData = [],
  kind = "category",
  contentKind,
  lazyLoading = false,
} = {}) {
  const ageRange = getAgeRangePublicRoute() || 120;
  const [items, setItems] = React.useState(initialData);
  const [hasMore, setHasMore] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(autoInit);
  const [page, setPage] = React.useState(initialPage);
  const limit = pageSize; // Number of items per page, adjust as necessary
  const init = useRef(false);

  const loadCategory = async (page) => {
    const controller = new AbortController();
    const { signal } = controller;

    try {
      setIsLoading(true);

      if (page > 1) {
        await new Promise((resolve) => setTimeout(resolve, fetchDelay));
      }

      const params = {
        pageIndex: limit,
        pageNumber: page,
        [kind]: decodeURIComponent(slug),
        contentAgeRange: ageRange,
      };

      // Lock content type when navigating from a specific showcase page
      if (contentKind) {
        params.kind = contentKind;
      }

      const response = await httpService.get(getCMSApiRoute("content"), {
        params,
        signal,
      });

      if (!response.statusText === "ok") {
        throw new Error("Network response was not ok");
      }

      const { data } = response.data;

      setHasMore(data?.total > page * limit);
      setItems(
        _.uniqBy(
          lazyLoading
            ? [
              ...items,
              ..._.shuffle(data?.results.filter((x) => x.status === 1)),
            ]
            : [...items, ...data?.results.filter((x) => x.status === 1)],
          (x) => x._id
        )
      );
    } catch (error) {
      setPage(page - 1);
      console.log(error);
      if (error.name === "AbortError") {
        console.log("Fetch aborted");
      } else {
        console.error("There was an error with the fetch operation:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (init.current === false && autoInit === true) {
      init.current = true;
      loadCategory(page);
    }
  }, []);

  const resetResults = () => {
    setItems(initialData);
    setPage(initialPage);
  };

  const onLoadMore = async () => {
    const newOffset = page + 1;

    setPage(newOffset);
    await loadCategory(newOffset);
  };

  return {
    items,
    hasMore,
    isLoading,
    onLoadMore,
    resetResults,
  };
}
